import {
  createSchemaPropertyList,
  type FormConfig,
  type ObjectSchema,
  type ProjectSchemaJSON,
  type PropertySchema
} from '@takeshape/schema';
import { NO_FORM_DISPLAYED_TYPES, NO_FORM_IGNORED_PROPERTIES } from '../../content/api.js';
import { getDefaultWidget } from '../../shape/defaults.js';
import { isImageSchema } from './is-image-schema.js';

/**
 * Return a comparator for strings that has an array of special values to put first if found
 */
export const makeLocaleSortWithFirst = (order: string[]) => (a: string, b: string) => {
  const aIndex = order.indexOf(a);
  const orderIncludesA = aIndex !== -1;
  const bIndex = order.indexOf(b);
  const orderIncludesB = bIndex !== -1;
  if (orderIncludesA && orderIncludesB) {
    return aIndex - bIndex;
  }

  if (orderIncludesA) {
    return -1;
  }

  if (orderIncludesB) {
    return 1;
  }

  return a.localeCompare(b);
};

export type ServiceContentEditSchemaValue = {
  contentSchema: PropertySchema;
  formConfig: FormConfig;
};

/**
 * Create a form for displaying the content edit page for a Shape that has no form.
 * Additionally returns a modified schema just to show titles. That can be removed after
 * https://app.shortcut.com/takeshape/story/8197/common-schema-form-widgets-should-use-the-name-if-the-title-is-missing
 */
export function createFallbackForm(runtimeSchema: ProjectSchemaJSON, shapeName: string): ServiceContentEditSchemaValue {
  const shape = runtimeSchema.shapes[shapeName];

  const contentSchema: ObjectSchema = {
    type: 'object',
    properties: {}
  };
  const formConfig: FormConfig = {
    properties: {},
    order: []
  };

  for (const [propName, propSchema] of createSchemaPropertyList(runtimeSchema, shape).getNodes()) {
    if (propName === '_id' || propName === '_shapeId' || NO_FORM_IGNORED_PROPERTIES.includes(propName)) {
      continue;
    }

    const isImage = isImageSchema(runtimeSchema, propSchema);
    if (isImage || NO_FORM_DISPLAYED_TYPES.includes(propSchema.type as string)) {
      contentSchema.properties[propName] = { ...propSchema, title: propName };
      formConfig.properties[propName] = {
        widget: isImage ? 'shopifyImage' : getDefaultWidget(propSchema)
      };
      formConfig.order.push(propName);
    }
  }

  formConfig.order = formConfig.order.sort(makeLocaleSortWithFirst(['id', 'name', 'title']));

  return {
    contentSchema,
    formConfig
  };
}
